import { useState, useEffect } from "react";

import {
  InternalContainer,
  ExternalContainer,
} from "../../components/Reusable/Layout";
import { UserDetailModal, CreateAdminModal } from "./component";
import { UsersTable, AdminSearchFilter } from "./component";
import { ADMIN_ROLE_VALUES } from "./component";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  fetchUsers,
  fetchFilterOptions,
  setFilters,
  clearFilters,
} from "../../features/auth/store/userSlice";
import {
  fetchCountries,
  selectCountries,
} from "../../features/auth/store/countrySlice";
import { RootState } from "../../redux/store";

const AdminManagement = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const [isCreateAdminModalOpen, setIsCreateAdminModalOpen] = useState(false);

  const dispatch = useAppDispatch();
  const countries = useAppSelector(selectCountries) || [];
  const {
    users,
    loading,
    total,
    perPage,
    currentPage,
    lastPage,
    filters,
    filterOptions,
  } = useAppSelector((state: RootState) => state.users);

  // Filter admin users from the full list
  const adminUsers = users.filter((user: any) =>
    ADMIN_ROLE_VALUES.includes(String(user.role || "").toLowerCase()),
  );

  // Fetch only admin-role users. The backend applies this scope before
  // pagination so each page and its totals remain accurate.
  useEffect(() => {
    dispatch(clearFilters());
    dispatch(
      fetchUsers({
        admin_only: true,
        page: 1,
        per_page: 15,
      }),
    );
    dispatch(fetchFilterOptions());
    dispatch(fetchCountries({ perPage: 250 }));
  }, [dispatch]);

  // Handle pagination navigation
  const handlePageChange = (newPage: number) => {
    if (!loading && newPage >= 1 && newPage <= lastPage) {
      dispatch(
        fetchUsers({
          ...filters,
          admin_only: true,
          page: newPage,
          per_page: 15,
        }),
      );
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    const newFilters = { [field]: value };
    dispatch(setFilters(newFilters));

    // Fetch users with updated filters
    dispatch(
      fetchUsers({
        ...filters,
        ...newFilters,
        admin_only: true,
        page: 1,
        per_page: 15,
      }),
    );
  };

  const handleUserClick = (user: any) => {
    setSelectedUser(user);
    setIsDetailsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDetailsModalOpen(false);
  };
  const handleCreateAdminClick = () => {
    setIsCreateAdminModalOpen(true);
  };
  const handleCloseCreateAdminModal = () => {
    setIsCreateAdminModalOpen(false);
  };

  const handleAdminOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsCreateAdminModalOpen(false);
    }
  };
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <ExternalContainer>
      <div className="w-full max-w-6xl min-h-screen px-2 sm:px-0">
        <InternalContainer headerText="Admin Management">
          <div className="flex flex-col gap-6">
            <AdminSearchFilter
              onClick={handleCreateAdminClick}
              onFilterChange={handleFilterChange}
              filters={filters}
              filterOptions={filterOptions}
              countries={countries}
              loading={loading}
            />

            <div className="border border-[#FFFFFF1A] rounded-xl overflow-hidden bg-transparent">
              <UsersTable
                users={adminUsers}
                onUserClick={handleUserClick}
                loading={loading}
                currentPage={currentPage}
                totalPages={lastPage}
                onPageChange={handlePageChange}
                total={total}
                perPage={perPage}
              />
            </div>
          </div>
        </InternalContainer>
      </div>

      {/* Modals downstream */}
      {selectedUser && isDetailsModalOpen && (
        <UserDetailModal
          user={selectedUser}
          onClose={handleCloseModal}
          showTabs={true}
          tabItems={["Personal Information", "Role"]}
        />
      )}
      <CreateAdminModal
        isOpen={isCreateAdminModalOpen}
        onClose={handleCloseCreateAdminModal}
        handleOverlayClick={handleAdminOverlayClick}
        handleModalClick={handleModalClick}
      />
    </ExternalContainer>
  );
};

export default AdminManagement;
